import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Plus, ArrowLeft, ShieldCheck, User as UserIcon, GraduationCap, MapPinned, Users as UsersIcon, Sparkles, Stars, Camera, Crown } from "lucide-react";
import Combobox from "@/components/umaang/Combobox";
import {
  RELIGIONS, CASTES, CASTES_ALL, EDUCATION, PROFESSIONS, DIETS, DIET_VALUES,
  MARITAL_STATUS, MOTHER_TONGUES, AGES, FAMILY_TYPES, FAMILY_VALUES,
  RASHIS, NAKSHATRAS, HOBBIES, CITIES, STATES, COUNTRIES, INCOME_BRACKETS,
} from "@/lib/refdata";

const FIELD = "border-[color:var(--umg-border)]";

function Section({ title, subtitle, children, icon: Icon, color = "#C85A53" }) {
  return (
    <div className="border p-6 relative overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
      <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10" style={{ background: color }} />
      <div className="relative flex items-start gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}18`, color }}>
            <Icon className="w-5 h-5" strokeWidth={1.8} />
          </div>
        )}
        <div>
          <h3 className="font-display text-2xl" style={{ color: Icon ? color : undefined }}>{title}</h3>
          {subtitle && <p className="text-xs text-[color:var(--umg-text-2)] mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-4 relative">{children}</div>
    </div>
  );
}

function Field({ label, children, wide, hint }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && <div className="text-[10px] text-[color:var(--umg-text-2)] mt-1">{hint}</div>}
    </div>
  );
}

function ChipList({ options, value = [], onChange }) {
  const on = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button
            type="button"
            key={o}
            onClick={() => on(o)}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${
              active ? "bg-[#C85A53] text-white border-[#C85A53]"
                     : "border-[color:var(--umg-border)] hover:bg-[color:var(--umg-secondary)]"
            }`}
          >{o}</button>
        );
      })}
    </div>
  );
}

export default function AdminUserEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${id}`);
        if (!alive) return;
        setForm(data);
        setPhotos(Array.isArray(data.photos) ? data.photos : []);
      } catch (e) { toast.error(formatError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (photos.length >= 10) { toast.error("Max 10 photos"); break; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        setPhotos((prev) => [...prev, `${backend}${data.url}`]);
      } catch {
        try {
          const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
          setPhotos((prev) => [...prev, b64]);
        } catch { toast.error("Upload failed"); }
      }
    }
    e.target.value = "";
  };
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, age: form.age ? Number(form.age) : undefined,
        gender: form.gender, mobile: form.mobile, email: form.email,
        education: form.education, profession: form.profession, income: form.income, diet: form.diet,
        address: form.address, pin_code: form.pin_code, city: form.city, state: form.state, country: form.country,
        marital_status: form.marital_status, has_children: form.has_children,
        children_count: form.children_count ? Number(form.children_count) : undefined,
        religion: form.religion, caste: form.caste, mother_tongue: form.mother_tongue,
        hobbies: Array.isArray(form.hobbies) ? form.hobbies : undefined,
        family_size: form.family_size ? Number(form.family_size) : undefined,
        family_type: form.family_type, about: form.about,
        aadhaar: form.aadhaar, pan: form.pan, photos,
        prefer_age_min: form.prefer_age_min ? Number(form.prefer_age_min) : undefined,
        prefer_age_max: form.prefer_age_max ? Number(form.prefer_age_max) : undefined,
        prefer_diet: form.prefer_diet, prefer_city: form.prefer_city,
        prefer_marital_status: form.prefer_marital_status ? (Array.isArray(form.prefer_marital_status) ? form.prefer_marital_status : [form.prefer_marital_status]) : undefined,
        prefer_religion: form.prefer_religion,
        dob: form.dob, birth_time: form.birth_time, birth_place: form.birth_place,
        rashi: form.rashi, nakshatra: form.nakshatra,
        is_premium: !!form.is_premium, kyc_verified: !!form.kyc_verified, banned: !!form.banned, hidden: !!form.hidden,
        hide_name: !!form.hide_name,
      };
      // Strip undefined
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await api.patch(`/admin/users/${id}`, payload);
      toast.success(`Profile updated for ${form.name || form.member_id}`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const availableCastes = form.religion && CASTES[form.religion] ? CASTES[form.religion] : CASTES_ALL;

  if (loading) return <div className="max-w-5xl mx-auto p-10 text-[color:var(--umg-text-2)]">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[color:var(--umg-text-2)] hover:text-[#C85A53]">
            <ArrowLeft className="w-3 h-3" /> Back to Admin
          </Link>
          <div className="mt-2 uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#DC2626] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">Admin · Full Profile Edit</div>
          <h1 className="font-display text-4xl mt-2">{form.name || form.member_id || "User"}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[color:var(--umg-text-2)]">
            {form.member_id && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#EEF2FF] to-[#F0FDF4] border border-[color:var(--umg-border)]"><span className="text-[10px] uppercase tracking-widest">ID</span><code className="text-[#7C3AED] font-semibold">{form.member_id}</code></span>}
            {form.email && <span>{form.email}</span>}
            {form.mobile && <span>· {form.mobile}</span>}
          </div>
        </div>
        <Button data-testid="admin-save-user-btn" disabled={saving} onClick={save} className="btn-pill bg-gradient-to-r from-[#DC2626] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-11 px-6 shadow-md shadow-[#DB2777]/30">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Section title="Admin controls" icon={ShieldCheck} color="#DC2626" subtitle="Toggle privileges — takes effect immediately.">
        <Field label="Premium member"><div className="flex items-center gap-3 pt-2"><Switch data-testid="admin-is-premium" checked={!!form.is_premium} onCheckedChange={(v) => setForm({ ...form, is_premium: v })} /><span className="text-sm text-[color:var(--umg-text-2)] flex items-center gap-1">{form.is_premium ? <><Crown className="w-3 h-3 text-[#B45309]" /> Premium</> : "Free"}</span></div></Field>
        <Field label="KYC verified"><div className="flex items-center gap-3 pt-2"><Switch data-testid="admin-kyc-verified" checked={!!form.kyc_verified} onCheckedChange={(v) => setForm({ ...form, kyc_verified: v })} /><span className="text-sm text-[color:var(--umg-text-2)]">{form.kyc_verified ? "Verified" : "Not verified"}</span></div></Field>
        <Field label="Banned"><div className="flex items-center gap-3 pt-2"><Switch data-testid="admin-banned" checked={!!form.banned} onCheckedChange={(v) => setForm({ ...form, banned: v })} /><span className="text-sm text-[color:var(--umg-text-2)]">{form.banned ? "Banned" : "Active"}</span></div></Field>
        <Field label="Hidden from feed"><div className="flex items-center gap-3 pt-2"><Switch data-testid="admin-hidden" checked={!!form.hidden} onCheckedChange={(v) => setForm({ ...form, hidden: v })} /><span className="text-sm text-[color:var(--umg-text-2)]">{form.hidden ? "Hidden" : "Visible"}</span></div></Field>
        <Field label="Hide real name (show Member ID only)" wide><div className="flex items-center gap-3 pt-2"><Switch data-testid="admin-hide-name" checked={!!form.hide_name} onCheckedChange={(v) => setForm({ ...form, hide_name: v })} /><span className="text-sm text-[color:var(--umg-text-2)]">{form.hide_name ? `Shown as ${form.member_id}` : `Real name visible`}</span></div></Field>
      </Section>

      <div className="border p-6 relative overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10 bg-[#0EA5E9]" />
        <div className="relative flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-[#0EA5E918] text-[#0EA5E9]"><Camera className="w-5 h-5" strokeWidth={1.8} /></div>
          <h3 className="font-display text-2xl text-[#0EA5E9]">Photos <span className="text-sm text-[color:var(--umg-text-2)]">({photos.length}/10)</span></h3>
        </div>
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 gap-3">
          {photos.map((src, i) => (
            <div key={`${src}-${i}`} className="relative aspect-square overflow-hidden border" style={{ borderRadius: 6, borderColor: "var(--umg-border)" }}>
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {photos.length < 10 && (
            <label className="aspect-square border border-dashed hover:bg-[color:var(--umg-secondary)] cursor-pointer grid place-items-center transition" style={{ borderRadius: 6, borderColor: "var(--umg-border)", background: "var(--umg-bg)" }}>
              <input type="file" accept="image/*" multiple hidden onChange={addPhoto} />
              <Plus className="w-6 h-6 text-[color:var(--umg-text-2)]" />
            </label>
          )}
        </div>
      </div>

      <Section title="Basic details" icon={UserIcon} color="#C85A53">
        <Field label="Name"><Input className={FIELD} value={form.name || ""} onChange={set("name")} /></Field>
        <Field label="Age"><Combobox value={form.age ? String(form.age) : ""} onChange={(v) => setForm({ ...form, age: Number(v) })} options={AGES.map((a) => ({ value: String(a), label: `${a} yrs` }))} placeholder="Select age" /></Field>
        <Field label="Gender"><Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}><SelectTrigger className={FIELD}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></Field>
        <Field label="Marital status"><Select value={form.marital_status || ""} onValueChange={(v) => setForm({ ...form, marital_status: v })}><SelectTrigger className={FIELD}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{MARITAL_STATUS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Mother tongue"><Combobox value={form.mother_tongue || ""} onChange={(v) => setForm({ ...form, mother_tongue: v })} options={MOTHER_TONGUES} placeholder="Select language" /></Field>
        <Field label="Religion"><Combobox value={form.religion || ""} onChange={(v) => setForm({ ...form, religion: v, caste: "" })} options={RELIGIONS} placeholder="Select religion" /></Field>
        <Field label="Caste / Community"><Combobox value={form.caste || ""} onChange={(v) => setForm({ ...form, caste: v })} options={availableCastes} placeholder="Select" /></Field>
        <Field label="Diet"><Combobox value={form.diet || ""} onChange={(v) => setForm({ ...form, diet: v })} options={DIETS.map((d) => ({ value: DIET_VALUES[d] || d, label: d }))} placeholder="Select" /></Field>
        <Field label="Email"><Input className={FIELD} type="email" value={form.email || ""} onChange={set("email")} /></Field>
        <Field label="Mobile"><Input className={FIELD} value={form.mobile || ""} onChange={set("mobile")} /></Field>
      </Section>

      <Section title="Education & Profession" icon={GraduationCap} color="#2563EB">
        <Field label="Highest qualification"><Combobox value={form.education || ""} onChange={(v) => setForm({ ...form, education: v })} options={EDUCATION} placeholder="Select qualification" /></Field>
        <Field label="Profession"><Combobox value={form.profession || ""} onChange={(v) => setForm({ ...form, profession: v })} options={PROFESSIONS} placeholder="Select profession" /></Field>
        <Field label="Annual income"><Combobox value={form.income || ""} onChange={(v) => setForm({ ...form, income: v })} options={INCOME_BRACKETS} placeholder="Select income range" /></Field>
      </Section>

      <Section title="Address" icon={MapPinned} color="#059669">
        <Field label="PIN code"><Input className={FIELD} inputMode="numeric" maxLength={6} value={form.pin_code || ""} onChange={(e) => setForm({ ...form, pin_code: e.target.value.replace(/\D/g, "").slice(0, 6) })} /></Field>
        <Field label="Country"><Combobox value={form.country || ""} onChange={(v) => setForm({ ...form, country: v })} options={COUNTRIES} placeholder="Select country" /></Field>
        <Field label="City"><Combobox value={form.city || ""} onChange={(v) => setForm({ ...form, city: v })} options={form.city && !CITIES.includes(form.city) ? [form.city, ...CITIES] : CITIES} placeholder="Select city" /></Field>
        <Field label="State"><Combobox value={form.state || ""} onChange={(v) => setForm({ ...form, state: v })} options={STATES} placeholder="Select state" /></Field>
        <Field label="Address" wide><Textarea rows={2} className={FIELD} value={form.address || ""} onChange={set("address")} /></Field>
      </Section>

      <Section title="Family" icon={UsersIcon} color="#B45309">
        <Field label="Family type"><Combobox value={form.family_type || ""} onChange={(v) => setForm({ ...form, family_type: v })} options={FAMILY_TYPES.map((f) => ({ value: FAMILY_VALUES[f] || f.toLowerCase(), label: f }))} placeholder="Select" /></Field>
        <Field label="Family size"><Input type="number" min={1} max={30} className={FIELD} value={form.family_size || ""} onChange={set("family_size")} /></Field>
        <Field label="Has children?"><div className="flex items-center gap-3 pt-2"><Switch checked={!!form.has_children} onCheckedChange={(v) => setForm({ ...form, has_children: v })} /><span className="text-sm text-[color:var(--umg-text-2)]">{form.has_children ? "Yes" : "No"}</span></div></Field>
        {form.has_children && <Field label="Children count"><Input type="number" min={1} max={12} className={FIELD} value={form.children_count || ""} onChange={set("children_count")} /></Field>}
      </Section>

      <Section title="About & interests" icon={Sparkles} color="#DB2777">
        <Field label="About" wide><Textarea rows={4} className={FIELD} value={form.about || ""} onChange={set("about")} /></Field>
        <Field label="Hobbies" wide><ChipList options={HOBBIES} value={Array.isArray(form.hobbies) ? form.hobbies : []} onChange={(v) => setForm({ ...form, hobbies: v })} /></Field>
      </Section>

      <Section title="Partner preferences" icon={Sparkles} color="#7C3AED">
        <Field label="Preferred age (min)"><Combobox value={form.prefer_age_min ? String(form.prefer_age_min) : ""} onChange={(v) => setForm({ ...form, prefer_age_min: Number(v) })} options={AGES.map((a) => ({ value: String(a), label: `${a} yrs` }))} placeholder="Min" /></Field>
        <Field label="Preferred age (max)"><Combobox value={form.prefer_age_max ? String(form.prefer_age_max) : ""} onChange={(v) => setForm({ ...form, prefer_age_max: Number(v) })} options={AGES.map((a) => ({ value: String(a), label: `${a} yrs` }))} placeholder="Max" /></Field>
        <Field label="Preferred religion"><Combobox value={form.prefer_religion || ""} onChange={(v) => setForm({ ...form, prefer_religion: v })} options={["Any", ...RELIGIONS]} placeholder="Select" /></Field>
        <Field label="Preferred diet"><Combobox value={form.prefer_diet || ""} onChange={(v) => setForm({ ...form, prefer_diet: v })} options={[{ value: "", label: "Any" }, ...DIETS.map((d) => ({ value: DIET_VALUES[d] || d, label: d }))]} placeholder="Select" /></Field>
        <Field label="Preferred city"><Combobox value={form.prefer_city || ""} onChange={(v) => setForm({ ...form, prefer_city: v })} options={["Any", ...CITIES]} placeholder="Select" /></Field>
        <Field label="Preferred marital status"><Select value={(form.prefer_marital_status || [])[0] || ""} onValueChange={(v) => setForm({ ...form, prefer_marital_status: [v] })}><SelectTrigger className={FIELD}><SelectValue placeholder="Any" /></SelectTrigger><SelectContent>{MARITAL_STATUS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></Field>
      </Section>

      <Section title="KYC" icon={ShieldCheck} color="#4F7942">
        <Field label="Aadhaar"><Input className={FIELD} value={form.aadhaar || ""} onChange={set("aadhaar")} placeholder="XXXX XXXX XXXX" /></Field>
        <Field label="PAN"><Input className={FIELD} value={form.pan || ""} onChange={set("pan")} placeholder="ABCDE1234F" /></Field>
      </Section>

      <Section title="Horoscope" icon={Stars} color="#F59E0B">
        <Field label="Date of birth"><Input type="date" className={FIELD} value={form.dob || ""} onChange={set("dob")} /></Field>
        <Field label="Birth time"><Input type="time" className={FIELD} value={form.birth_time || ""} onChange={set("birth_time")} /></Field>
        <Field label="Birth place"><Input className={FIELD} value={form.birth_place || ""} onChange={set("birth_place")} /></Field>
        <Field label="Rashi"><Combobox value={form.rashi || ""} onChange={(v) => setForm({ ...form, rashi: v })} options={RASHIS.map((r) => ({ value: r.split(" ")[0], label: r }))} placeholder="Select" /></Field>
        <Field label="Nakshatra"><Combobox value={form.nakshatra || ""} onChange={(v) => setForm({ ...form, nakshatra: v })} options={NAKSHATRAS} placeholder="Select" /></Field>
      </Section>

      <div className="flex justify-end pt-2">
        <Button data-testid="admin-save-user-btn-2" disabled={saving} onClick={save} className="btn-pill bg-gradient-to-r from-[#DC2626] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-11 px-8 shadow-md shadow-[#DB2777]/30">
          {saving ? "Saving…" : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}
